import type { RefObject } from 'react'
import { createFirstOnPage } from '../../lib/firstOnPage'

/**
 * The reassurance: a reader looking at something that could not load must never
 * wonder whether it is gone.
 *
 * Two owner rulings shape it (Spec 2 D5, #212). **One neutral sentence for every
 * subject.** It used to end "not your record", which was wrong on Members,
 * Groups, the permission list, Invites, Credentials, the brand list and the
 * reference curves, none of which is the reader's record. **Said once per page,
 * by the first note in reading order** (`useSaysReassurance`). Every note used to
 * repeat it, four times on a fully failed Journey.
 *
 * The notes without it still read as failures and never as empty states: the
 * "Could not load" label, the terracotta wash, "could not be loaded just now"
 * and Try again all stay. This module is the wording's only home, so no empty
 * state can borrow it (PanelError.test.tsx sweeps the source for a second copy).
 * It lives beside `PanelError` rather than in it only because a component file
 * that also exports a hook is not a Fast Refresh boundary.
 */
export const REASSURANCE = 'Nothing has been lost: only the request failed.'

/**
 * Which note on the page says it: the first in reading order. The mechanism is
 * `lib/firstOnPage.ts`, shared with the busy line (#392), which says its own
 * thing once per page the same way; this is its instance for the notes.
 */
const useSaysReassurance = createFirstOnPage()

/**
 * A note's prose: its own `lead`, followed by the reassurance when this is the
 * note on the page that says it. Every note that can carry the sentence builds
 * its prose here, so "which note says it, and how it joins the lead" has one
 * home. An empty string means the note has nothing to say beyond its title.
 */
export function useReassured(ref: RefObject<HTMLElement | null>, lead?: string): string {
  const says = useSaysReassurance(ref)
  return [lead, says ? REASSURANCE : undefined].filter(Boolean).join(' ')
}
